import * as React from 'react';

/**
 * Calls `fn` immediately (when enabled) and then every `intervalMs`.
 * Pauses while the tab is hidden (`document.hidden`) and resumes — with an
 * immediate call — when it becomes visible again. Cleans up on unmount and
 * whenever `enabled`/`intervalMs` change. Overlapping runs are avoided: a new
 * tick is skipped while the previous async `fn` is still in flight.
 */
export function usePoll(
  fn: () => void | Promise<void>,
  intervalMs: number,
  enabled = true,
): void {
  const fnRef = React.useRef(fn);
  fnRef.current = fn;

  React.useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    let inFlight = false;

    const run = async () => {
      if (cancelled || inFlight || document.hidden) return;
      inFlight = true;
      try {
        await fnRef.current();
      } finally {
        inFlight = false;
      }
    };

    const start = () => {
      if (timer != null) return;
      void run(); // fire immediately on (re)start
      timer = setInterval(() => { void run(); }, intervalMs);
    };

    const stop = () => {
      if (timer != null) { clearInterval(timer); timer = null; }
    };

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };

    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, enabled]);
}
